import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { CheckCircle, XCircle, Clock, Calendar, User, Mail, Building, Tag } from 'lucide-react'

interface WebhookLog {
  id: string
  created_at: string
  title: string
  description: string
  activity_type: string
  icon: string
}

interface WebhookDetailModalProps {
  webhook: WebhookLog | null
  isOpen: boolean
  onClose: () => void
}

export function WebhookDetailModal({ webhook, isOpen, onClose }: WebhookDetailModalProps) {
  if (!webhook) return null

  const getStatusIcon = (activityType: string) => {
    switch (activityType) {
      case 'webhook_success':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'webhook_error':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />
    }
  }

  const getStatusColor = (activityType: string) => {
    if (activityType === 'webhook_error') return 'destructive'
    if (activityType === 'webhook_success') return 'default'
    return 'secondary'
  }

  const parseWebhookDetails = (description: string) => {
    const details: Record<string, string> = {}
    const parts = description.split(' | ')
    
    parts.forEach(part => {
      const [key, value] = part.split(': ')
      if (key && value) {
        details[key.trim()] = value.trim()
      }
    })
    
    return details
  }

  const details = parseWebhookDetails(webhook.description)
  const isWebhookData = webhook.activity_type === 'webhook_success' || webhook.activity_type === 'webhook_error'

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {getStatusIcon(webhook.activity_type)}
            <span>{webhook.title}</span>
            <Badge variant={getStatusColor(webhook.activity_type)}>
              {webhook.activity_type.replace('webhook_', '')}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Timestamp */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{new Date(webhook.created_at).toLocaleString()}</span>
          </div>

          {/* Webhook Details */}
          {isWebhookData && Object.keys(details).length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Webhook Details</h3>
              
              <div className="grid gap-4">
                {details.TO && (
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Recipient Email</p>
                      <p className="text-sm text-muted-foreground break-all">{details.TO}</p>
                    </div>
                  </div>
                )}

                {details.FROM && (
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <User className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Sender Email</p>
                      <p className="text-sm text-muted-foreground break-all">{details.FROM}</p>
                    </div>
                  </div>
                )}

                {details.CAMPAIGN && (
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Building className="w-5 h-5 text-purple-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Campaign Name</p>
                      <p className="text-sm text-muted-foreground break-all">{details.CAMPAIGN}</p>
                    </div>
                  </div>
                )}

                {details.CLIENT && (
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Building className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Client ID</p>
                      <p className="text-sm text-muted-foreground">{details.CLIENT}</p>
                    </div>
                  </div>
                )}

                {details.CATEGORY && (
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Tag className="w-5 h-5 text-pink-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Lead Category</p>
                      <Badge variant="outline" className="mt-1">{details.CATEGORY}</Badge>
                    </div>
                  </div>
                )}

                {details.ERROR && (
                  <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-red-800">Error Message</p>
                      <p className="text-sm text-red-600 break-all">{details.ERROR}</p>
                    </div>
                  </div>
                )}

                {details.Stack && (
                  <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-red-800">Stack Trace</p>
                      <pre className="text-xs text-red-600 break-all whitespace-pre-wrap">{details.Stack}</pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm">{webhook.description}</p>
            </div>
          )}

          {/* Summary Stats */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Status:</span>
                <Badge variant={getStatusColor(webhook.activity_type)} className="ml-2">
                  {webhook.activity_type === 'webhook_success' ? 'Success' : 
                   webhook.activity_type === 'webhook_error' ? 'Failed' : 'Processing'}
                </Badge>
              </div>
              <div>
                <span className="font-medium">Processed At:</span>
                <span className="ml-2">{new Date(webhook.created_at).toLocaleTimeString()}</span>
              </div>
              {isWebhookData && details.CATEGORY && (
                <div>
                  <span className="font-medium">Lead Status:</span>
                  <span className="ml-2">{details.CATEGORY}</span>
                </div>
              )}
              {isWebhookData && details.CLIENT && (
                <div>
                  <span className="font-medium">Client:</span>
                  <span className="ml-2">#{details.CLIENT}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}